import { useState, useRef, useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import gtm from 'eco-design-reliance/gtm';

import { useRouter } from 'next/router';
import { GenerateIcon } from './generateIcon';
import GenLoading from './GenLoading';
import styles from './index.module.scss';
import createTraceId from '@/utils/singUtil/generateTraceId';
import getLangFromLocale from '@/utils/singUtil/getLangFromLocale';
import { loginUser } from '@/utils/cookie';
import { useLang } from '@/i18n';

const NEXT_PUBLIC_API_HOST = process.env.NEXT_PUBLIC_API_HOST;
const websiteUrl = process.env.NEXT_PUBLIC_WEB_HOST;
const homeUrl = process.env.NEXT_PUBLIC_HOME_BATTERY_DOMAIN;

export default function AIGenerate({ keyword }: { keyword: string }) {
    const [generateLoading, setGenerateLoading] = useState(false);
    const controller: any = useRef<AbortController>(); // 用于取消流式响应请求
    const [requestStatus, setRequestStatus] = useState('beforePending'); // 请求状态, 用来判断是否显示空结果的样式
    const userInfo = loginUser() || {};
    const [text, setText] = useState(''); // 流式响应内容
    const [products, setProducts] = useState<any[]>([]); // 生成式内容中的产品
    const [streamStatus, setStreamStatus] = useState('standby'); // 消息推送状态
    const { locale } = useRouter();
    const uuid: any = useRef<string>(''); //  sessionId

    const { t } = useLang('shared');

    const fetchStreamData = async (sessionId?: string) => {
        if (!!controller?.current) {
            controller?.current?.abort();
        }

        setGenerateLoading(true);
        setRequestStatus('pending');

        controller.current = new AbortController();
        const { signal } = controller.current;
        const { appId } = getLangFromLocale(locale as string) as any;

        const initHeaders: any = {
            'Content-Type': 'application/json',
            'x-appid': appId,
        };

        const data = {
            tid: createTraceId(),
            userId: userInfo?.user?.userId,
            sessionId: sessionId || uuid?.current,
            query: keyword,
            appid: appId,
        };

        const mockData = {
            tid: '25120314091685184161363044715214',
            appid: '1',
            sessionId: '03c5f22b-5a22-466c-82bd-89bb27807902',
            query: 'Powerstream',
        };

        try {
            // 使用 @microsoft/fetch-event-source 来处理流式响应
            // await fetchEventSource(`http://10.20.30.160/website/aiSearch/homeGenai`, {
            await fetchEventSource(`${NEXT_PUBLIC_API_HOST}/aiSearch/homeGenai`, {
                signal: signal, // 传入 AbortController 的 signal
                method: 'POST',
                headers: initHeaders,
                body: JSON.stringify(data),

                onopen(e: any): Promise<void> {
                    if (!!text) {
                        setText('');
                        setProducts([]);
                    }

                    setStreamStatus('open');

                    // ga4_push({
                    //     event: 'ga4Event',
                    //     event_name: 'ai_search_trigger',
                    //     event_parameters: {
                    //         search_term: keyword,
                    //     },
                    // });

                    return Promise.resolve();
                },
                onmessage(event: any) {
                    setStreamStatus('message');
                    setGenerateLoading(false);
                    // 每次收到消息时，event.data 会是服务器推送的内容
                    try {
                        const json = JSON.parse(event.data);
                        // 提取 content 并逐字显示
                        if (json?.data && json?.data && json?.data?.content !== null && json?.data?.content !== undefined) {
                            const content = json?.data?.content;
                            const doneStatus = json?.data?.done;
                            const errorCode: any = json?.data?.code;
                            const productsData = json.data.products || [];

                            if (errorCode === '1028') {
                                // 触发错误码
                                setStreamStatus('close');
                                controller?.current?.abort(); // 遇到错误时关闭连接
                                setText('');
                                setProducts([]);
                                setRequestStatus('fulfilled');
                                setGenerateLoading(false);
                                return;
                            }
                            if (!!doneStatus) {
                                // 所有内容显示完后，渲染 products
                                setProducts(productsData);
                            }

                            // 本次直接显示内容
                            setText((prevText) => {
                                return prevText + content;
                            });
                        }
                    } catch (error) {
                        setStreamStatus('error');
                        console.error('解析错误:', error);
                    }
                },
                onclose() {
                    setStreamStatus('close');
                    setGenerateLoading(false);
                    setRequestStatus('fulfilled');
                },
                onerror(error: any) {
                    setRequestStatus('fulfilled');
                    setStreamStatus('error');
                    setText('');
                    setProducts([]);
                    setGenerateLoading(false);
                    controller?.current?.abort(); // 遇到错误时关闭连接
                    throw error;
                },
            });
        } catch (error) {
            setRequestStatus('fulfilled');
            setStreamStatus('error');
            setText('');
            setProducts([]);
            setGenerateLoading(false);
            controller?.current?.abort(); // 遇到错误时关闭连接
        }
    };

    const handleLearnMoreClick = (product: any) => {
        if (typeof window !== 'undefined') {
            const searchEntry: any = localStorage.getItem('search_entry');
            gtm.push({
                event: 'uaEvent',
                event_name: 'select_product',
                list_name: 'none',
                product_name: product?.content?.spuName,
                button_name: t('support.site_support_learn_more'),
                position: `search_${product?.content?.spuName}`,
                navigation_name: 'none',
                source_method: searchEntry || '',
            });
        }
    };

    const handleBuyNowClick = (product: any) => {
        if (typeof window !== 'undefined') {
            const searchEntry: any = localStorage.getItem('search_entry');

            gtm.push({
                event: 'uaEvent',
                event_name: 'buy',
                product_name: product?.content?.spuName,
                position: 'search_explore_top',
                view_page_type: 'shopify PDP',
                navigation_name: 'none',
                source_method: searchEntry || '',
            });
        }
    };

    const handleProductLink = (product: any) => {
        if (product?.content?.domainType === 'ENTERPRISE') {
            return `${homeUrl}/${locale}/${product?.content?.learnLink}`;
        }
        return `${websiteUrl}/${locale}/${product?.content?.learnLink}`;
    };

    useEffect(() => {
        if (streamStatus === 'open' || streamStatus === 'message' || controller?.current) {
            controller?.current?.abort();
        }

        if (typeof window !== 'undefined') {
            if (!!sessionStorage.getItem('ef_search_ai_session_id')) {
                const currentSessionId: any = sessionStorage.getItem('ef_search_ai_session_id');
                uuid.current = currentSessionId;

                if (!!keyword) {
                    fetchStreamData(currentSessionId);
                } else {
                    controller?.current?.abort();
                }
            }
        }

        if (typeof window !== 'undefined') {
            const searchEntry: any = localStorage.getItem('search_entry');

            gtm.push({
                event: 'uaEvent',
                event_name: 'ai_expose',
                source_method: searchEntry || '',
            });
        }

        return () => {
            if (streamStatus === 'open' || streamStatus === 'message' || controller?.current) {
                controller?.current?.abort();
            }
        };
    }, [keyword]);

    return (
        <div className={styles.generateBox}>
            <GenerateIcon />
            <div className="generateContent">
                {!!generateLoading && (
                    <div className="generateContentWrapper">
                        <GenLoading />
                    </div>
                )}

                {!!text && !generateLoading && !!keyword && (
                    <div className="generateContentWrapper">
                        <div className="generateContentText">{text}</div>
                    </div>
                )}

                {!!keyword && !text && !generateLoading && requestStatus === 'fulfilled' && (
                    <div className="generateEmptyContentWrapper">
                        <p className="generateContentEmptyText">{t('support.site_support_ai_empty_text')}</p>
                    </div>
                )}
            </div>

            <div className="generateContentProducts">
                {products?.slice(0, 4)?.map((product: any) => {
                    const imageInfo = JSON.parse(product?.content?.images);

                    const imagePath =
                        imageInfo?.transparentImages ||
                        imageInfo?.images ||
                        imageInfo?.navigatorImages ||
                        'https://websiteoss.ecoflow.com/img/search/2461bcec7e9c85a041ff2f95d5e476ea.png';

                    return (
                        <div key={product?.id} className="generateContentProduct">
                            <div className="generateContentProductImage">
                                <EcoImg pc={imagePath} alt={product?.content?.spuName} />
                            </div>
                            <div className="generateContentProductInfo">
                                <div className="generateContentProductInfoContent">
                                    {product?.content?.label && <p className="generateContentProductLabel">{product?.content?.label}</p>}

                                    <div className="generateContentProductName">{product?.content?.spuName}</div>
                                    <div className="generateContentProductDescription">{product?.content?.description}</div>
                                </div>

                                <div className="generateContentProductItemLinks">
                                    {product?.content?.learnLink && (
                                        <EcoLink href={handleProductLink(product)} onClick={() => handleLearnMoreClick(product)}>
                                            <span className="font-family-medium link">{t('support.site_support_learn_more')}</span>
                                        </EcoLink>
                                    )}

                                    {product?.content?.buyLink && (
                                        <EcoLink href={product?.content?.buyLink} onClick={() => handleBuyNowClick(product)}>
                                            <span className="font-family-medium link">{t('support.site_support_buy_now')}</span>
                                        </EcoLink>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
